import { FC } from 'react';
import type { PaymentHistoryItem } from '@teachur/http-client';
import { InvoiceRow } from './InvoiceRow';

interface InvoicesSectionProps {
  payments: PaymentHistoryItem[];
}

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);

export const InvoicesSection: FC<InvoicesSectionProps> = ({ payments }) => {
  if (!payments?.length) return null;

  const paidPayments = payments.filter(
    (payment) => payment.status === 'paid' || payment.status === 'succeeded'
  );
  const currency = paidPayments[0]?.currency ?? 'usd';
  const totalPaid = paidPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);

  return (
    <div className="flex flex-col gap-4 mt-6">
      <div className="flex items-center justify-between">
        <h3 className="[font-family:var(--font-arvo)] [font-size:18px] font-bold text-(--neutral-black-NB1000)! m-0">
          Invoices
        </h3>
        {paidPayments.length > 0 && (
          <span className="[font-family:var(--font-montserrat)] [font-size:13px] text-(--neutral-black-NB700)">
            Total paid:{' '}
            <span className="font-semibold text-(--neutral-black-NB1000)">
              {formatAmount(totalPaid, currency)}
            </span>
          </span>
        )}
      </div>

      <div className="rounded-lg border border-solid border-(--neutral-white-NW600) overflow-x-auto overflow-y-auto max-h-[360px]">
        <table className="w-full border-collapse min-w-[600px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Invoice
              </th>
              <th className="text-left px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Date
              </th>
              <th className="text-left px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Description
              </th>
              <th className="text-right px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Amount
              </th>
              <th className="text-center px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Status
              </th>
              <th className="text-right px-4 py-3 [font-family:var(--font-montserrat)] [font-size:12px] font-semibold text-(--neutral-black-NB700) uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <InvoiceRow key={payment.id} payment={payment} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
